/**
 * EZ-IOK 간편인증 서비스
 * 드림시큐리티 EZ-IOK 표준창/통합창 연동
 *
 * @see https://ez-iok.com/guide/eziok_std_guide/
 */

// 백엔드 API URL (환경변수에서 가져오거나 기본값 사용)
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.ondoc.co.kr';

// EZ-IOK 콜백 URL (EZ-IOK 서버에 등록된 URL과 일치해야 함)
const EZIOK_CALLBACK_URL = `${API_BASE}/func/eziok/callback`;

declare global {
  interface Window {
    // 표준창: SDK가 인증사업자 선택 UI 제공
    eziok_std_process?: (
      requestUrl: string,
      browserType: string,
      callbackFuncName: string,
    ) => void;
    // 통합창: 인증사업자 미리 지정, 선택 UI 스킵
    eziok_std_process1?: (
      requestUrl: string,
      browserType: string,
      callbackFuncName: string,
    ) => void;
  }
}

// 인증사업자 정보 (백엔드 API 응답 형식에 맞춤)
export interface EZIOKProvider {
  code: string;        // 인증사업자 코드 (kakao, naver 등)
  name: string;        // 인증사업자명
  type: string;        // 타입 (민간, 통신사, 금융, 공동인증)
  ciStorable: boolean; // CI 저장 가능 여부 (false면 ID/PW찾기 용도만)
}

// 인증 요청 파라미터
export interface EZIOKRequestParams {
  userId?: number;           // 사용자 ID (로그인 상태면 전달)
  authProviderCode: string;  // 인증사업자 코드 (필수)
  resultUrl?: string;        // 결과 수신 URL
  serviceType?: 'auth' | 'auth_ext' | 'auth_adult' | 'sign';
  retType?: 'callback' | 'redirect';
  retTransferType?: 'keytoken' | 'hubtoken';
}

// 인증 결과 (postMessage로 수신)
export interface EZIOKResult {
  success: boolean;
  message: string;
  data: {
    name?: string;
    ci?: string;
    di?: string;
    birthDate?: string;
    gender?: string;
    nationalInfo?: string;
    mobileNo?: string;
  };
}

// SDK 로드 상태
type SDKStatus = 'idle' | 'loading' | 'loaded' | 'error';

class EZIOKService {
  private sdkStatus: SDKStatus = 'idle';
  private sdkLoadPromise: Promise<void> | null = null;

  // 개발/운영 환경 구분
  private readonly isDev =
    process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost';

  // SDK URL (개발: scert, 운영: cert)
  private readonly SDK_URL = this.isDev
    ? 'https://scert.ez-iok.com/stdauth/ds_auth_ptb/asset/js/ptb_ezauth_proc.js'
    : 'https://cert.ez-iok.com/stdauth/ds_auth_ptb/asset/js/ptb_ezauth_proc.js';

  /**
   * EZ-IOK SDK 스크립트 로드
   */
  loadScript(): Promise<void> {
    if (this.sdkStatus === 'loaded') {
      return Promise.resolve();
    }

    if (this.sdkLoadPromise) {
      return this.sdkLoadPromise;
    }

    this.sdkStatus = 'loading';
    this.sdkLoadPromise = new Promise((resolve, reject) => {
      // 이미 로드된 경우
      if (window.eziok_std_process || window.eziok_std_process1) {
        this.sdkStatus = 'loaded';
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = this.SDK_URL;
      script.async = true;

      script.onload = () => {
        this.sdkStatus = 'loaded';
        console.log('[EZIOK] SDK loaded successfully');
        resolve();
      };

      script.onerror = () => {
        this.sdkStatus = 'error';
        this.sdkLoadPromise = null;
        const error = new Error('[EZIOK] Failed to load SDK');
        console.error(error);
        reject(error);
      };

      document.head.appendChild(script);
    });

    return this.sdkLoadPromise;
  }

  /**
   * 통합창 실행 (인증사업자 미리 지정, 선택 UI 스킵)
   * @param requestUrl 백엔드 요청 URL (authProviderCode 포함)
   * @param browserType 브라우저 타입 (WB=PC웹, MB=모바일웹, MWV=앱 WebView)
   * @param callbackFuncName 콜백 함수명 (문자열)
   */
  async processIntegrated(
    requestUrl: string,
    browserType: string = 'WB',
    callbackFuncName: string = '',
  ): Promise<void> {
    await this.loadScript();

    if (!window.eziok_std_process1) {
      throw new Error('[EZIOK] SDK (eziok_std_process1) not available');
    }

    console.log('[EZIOK] Calling eziok_std_process1 with:', {
      requestUrl,
      browserType,
      callbackFuncName,
    });

    window.eziok_std_process1(requestUrl, browserType, callbackFuncName);
  }

  /**
   * 클라이언트 txId 생성
   * 형식: EZIOK_{userId}_{timestamp}_{random}
   */
  private generateClientTxId(userId: number): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    return `EZIOK_${userId}_${timestamp}_${random}`;
  }

  /**
   * 간편인증 시작 (통합창 방식)
   * 프론트엔드에서 txId를 생성하여 백엔드로 전달
   */
  async startAuth(params: EZIOKRequestParams): Promise<void> {
    // TODO: 테스트용 userId 고정 (나중에 실제 로그인 사용자 ID로 변경)
    const userId = params.userId || 21;

    // 프론트엔드에서 txId를 먼저 생성
    const clientTxId = this.generateClientTxId(userId);

    // sessionStorage에 txId 저장 (결과 조회 시 사용)
    sessionStorage.setItem('eziok_txId', clientTxId);
    console.log('[EZIOK] Generated clientTxId:', clientTxId);

    // localStorage에서 인증 토큰 가져오기
    // SDK가 직접 URL을 호출하므로 axiosClient 인터셉터가 적용되지 않음
    const accessToken = localStorage.getItem('token');

    // 백엔드 요청 URL (SDK가 직접 호출)
    // EZ-IOK는 keytoken/callback 방식에서도 resultUrl 검증 필요
    const resultUrl = params.resultUrl || EZIOK_CALLBACK_URL;
    const queryParams = new URLSearchParams({
      userId: String(userId),
      authProviderCode: params.authProviderCode,
      serviceType: params.serviceType || 'auth',
      retType: params.retType || 'callback',
      retTransferType: params.retTransferType || 'keytoken',
      clientTxId: clientTxId, // 프론트에서 생성한 txId 전달
      resultUrl: resultUrl,   // EZ-IOK 서버에서 등록된 URL과 일치 필요
    });

    // 인증 토큰이 있으면 쿼리 파라미터로 추가
    if (accessToken) {
      queryParams.append('access_token', accessToken);
    }

    const requestUrl = `${API_BASE}/func/eziok/request?${queryParams.toString()}`;
    const browserType = this.detectBrowserType();

    console.log('[EZIOK] Starting auth with:', { requestUrl, browserType, clientTxId });

    await this.processIntegrated(requestUrl, browserType, 'eziokCallback');
  }

  /**
   * SDK 로드 상태 확인
   */
  isLoaded(): boolean {
    return this.sdkStatus === 'loaded' && !!(window.eziok_std_process || window.eziok_std_process1);
  }

  /**
   * 디바이스 타입 감지
   * WB: PC 웹, MB: 모바일 웹, MWV: 앱 WebView
   */
  detectBrowserType(): 'WB' | 'MB' | 'MWV' {
    // Flutter 앱 WebView 체크 (localStorage + flutter_inappwebview)
    const isFlutterApp = localStorage.getItem('mobileApp') !== null &&
                         typeof (window as any).flutter_inappwebview !== 'undefined';

    if (isFlutterApp) {
      return 'MWV';
    }

    const userAgent = navigator.userAgent.toLowerCase();

    // 기타 WebView 감지 (fallback)
    const isWebView =
      userAgent.includes('mobileapp') ||
      userAgent.includes('wv') ||
      ((userAgent.includes('iphone') || userAgent.includes('ipad')) &&
        !userAgent.includes('safari'));

    if (isWebView) {
      return 'MWV';
    }

    // 모바일 브라우저 감지
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    return isMobile ? 'MB' : 'WB';
  }
}

export default new EZIOKService();